<script setup lang="ts">
import ReorderableTable from '@/components/ReorderableTable.vue';
import type { ConsultationDto } from '@/services/types/api.types';
import { useCommunityStore, useConsultationsStore, useConsultationTemplatesStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import CreateConsultationTemplateDialog from '@/components/dialogs/CreateConsultationTemplateDialog.vue';
import CreateConsultationDialog from '@/components/dialogs/CreateConsultationDialog.vue';
import { Constants } from '@/constants';
import { getTranslation } from '@/composables/data-display-utils';
import { confirmDelete } from '@/helpers/dialogs.helper';

const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

const consultationsStore = useConsultationsStore();
const consultationTemplatesStore = useConsultationTemplatesStore();
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);
consultationTemplatesStore.fetchAll();

const { isLoading, consultationTemplates } = storeToRefs(consultationTemplatesStore);

const actionMenuItems = computed(() => [
  { action: 'create-consultation', label: t('consultationTemplate.createConsultation') },
  { action: 'delete-row', label: t('global.label.delete') },
]);

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ConsultationDto) => getTranslation(row, 'title', row.language_enabled),
    align: 'left',
  },
]);

function getDefaultLanguageEnabled() {
  return [community.value?.default_language ?? Constants.DefaultLocale];
}

const handleRowClick = (row: ConsultationDto) => {
  router.push({ name: 'consultation_template_details', params: { id: row.id } });
};

const handleDeleteRow = (row: ConsultationDto) => {
  if (!row.id) {
    console.warn('Row does not have an id');
    return;
  }

  confirmDelete(t('consultationTemplate.name')).onOk(() => {
    consultationTemplatesStore.delete(row.id!);
  });
};

const handleActions = async (action: any) => {
  switch (action.action) {
    case 'create-consultation':
      const consultationTemplateId = action.payload.row.id;
      await consultationTemplatesStore.fetchById(consultationTemplateId);
      const consultationTemplate = consultationTemplatesStore.getItem(consultationTemplateId);
      $q.dialog({
        component: CreateConsultationDialog,
        componentProps: {
          consultation: consultationsStore.createConsultation({
            title: consultationTemplate?.title,
            description: consultationTemplate?.description,
            cloneFromId: consultationTemplate?.id,
            language_enabled: consultationTemplate?.language_enabled ?? getDefaultLanguageEnabled(),
          }),
          languageEnabled: getDefaultLanguageEnabled(),
        },
      });
      break;
  }
};

const handleAddRow = () => {
  const consultationTemplate: ConsultationDto = consultationTemplatesStore.create({
    language_enabled: getDefaultLanguageEnabled(),
  });
  $q.dialog({
    component: CreateConsultationTemplateDialog,
    componentProps: {
      consultationTemplate,
    },
  });
};
</script>

<template>
  <div>
    <h1 class="title">{{ t('consultationTemplate.consultationTemplates') }}</h1>
    <reorderable-table
      :rows="consultationTemplates"
      :columns="columns"
      :loading="isLoading"
      itemKey="id"
      :disableOrderable="true"
      :disableDeleteButton="true"
      :pagination-enabled="false"
      :action-menu-items="actionMenuItems"
      @row-click="handleRowClick"
      @delete-row="handleDeleteRow"
      @add-row="handleAddRow"
      @actions="handleActions"
    />
  </div>
</template>
<style lang="scss"></style>
